import { Link } from "react-router-dom";
import { Instagram, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function Footer() {
  const content = useSiteContent();

  return (
    <footer data-testid="site-footer" className="bg-[#060608] border-t border-[#D4AF37]/15">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-4">
              <img src="/images/logo-passion.png" alt="Passion Photographie" data-testid="footer-logo"
                   className="h-20 w-20 object-contain"
                   style={{ filter: "drop-shadow(0 0 16px rgba(212,175,55,0.3))" }} />
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Passion Photographie</p>
                <p className="font-script text-2xl text-[#F8F9FA] mt-1">Créer. Capturer. Raconter.</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 leading-relaxed">
              Une passion pour l'image, la lumière et la créativité. Chaque photographie devient une façon unique de
              raconter une histoire et de faire naître une émotion.
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37] mt-8 mb-5">Suivez mon travail</p>
            <div className="flex gap-4" data-testid="footer-socials">
              
              <a href={content.instagram_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram" aria-label="Instagram"
                 className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                <Instagram size={16} />
              </a>
              <a href={content.youtube_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="footer-social-youtube" aria-label="YouTube"
                 className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                <Youtube size={16} />
              </a>
              <a href={content.tiktok_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="footer-social-tiktok" aria-label="TikTok"
                 className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.94 2.94 0 0 1-5.19 1.93 2.94 2.94 0 0 1 2.31-4.94c.3 0 .6.04.88.13V9.24a6.33 6.33 0 0 0-.88-.06 6.39 6.39 0 0 0-5.4 9.74 6.39 6.39 0 0 0 10.74-6.9V8.69a8.16 8.16 0 0 0 4.77 1.52V6.75c-.22 0-.43-.02-.64-.06z"/>
                </svg>
              </a>
            </div>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-6">Navigation</p>
            <nav className="flex flex-col gap-3" data-testid="footer-menu">
              {[["/", "Accueil"], ["/portfolio", "Portfolio"], ["/a-propos", "À propos"], ["/services", "Services"], ["/tarifs", "Tarifs"], ["/contact", "Contact"]].map(([to, label]) => (
                <Link key={to} to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-slate-400 text-sm hover:text-[#F3E5AB] transition-colors w-fit">
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-6">Coordonnées</p>
            <div className="flex flex-col gap-4 text-sm">
              <a href="tel:+15147818360" data-testid="footer-phone-link" className="flex items-center gap-3 text-slate-300 hover:text-[#F3E5AB] transition-colors">
                <Phone size={15} className="text-[#D4AF37]" /> 514-781-8360
                <span className="text-slate-500 text-xs">— Appel ou texto</span>
              </a>
              <a href="mailto:photographedanieldulude@gmail.com" data-testid="footer-email-link" className="flex items-center gap-3 text-slate-300 hover:text-[#F3E5AB] transition-colors break-all">
                <Mail size={15} className="text-[#D4AF37] shrink-0" /> photographedanieldulude@gmail.com
              </a>
              <p className="flex items-center gap-3 text-slate-400">
                <MapPin size={15} className="text-[#D4AF37] shrink-0" /> Saint-Jean-sur-Richelieu, Montérégie, Québec
              </p>
            </div>
          </div>
        </div>

        <div className="gold-line mt-14 mb-8" />
        <p className="text-center text-slate-500 text-xs tracking-wider" data-testid="footer-copyright">
          © 2026 Daniel Dulude | Photographe — Saint-Jean-sur-Richelieu, Montérégie, Québec, Canada
        </p>
      </div>
    </footer>
  );
}
